// ICC is the only login service, reached through this site's own backend; keep this file identical in the Harinaam and Brihad Mridanga frontends.
import axios from "axios";

const icc = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}/api/icc-auth/`,
  timeout: 30000,
});

const unwrap = (request) => request.then((res) => res.data);

// The access token lives under "token" so the existing request interceptor keeps working unchanged.
function saveSession(data) {
  localStorage.setItem("token", data.access_token);
  localStorage.setItem("refresh_token", data.refresh_token);
  return data;
}

export function clearSession() {
  ["token", "refresh_token", "user", "account_id"].forEach((key) => localStorage.removeItem(key));
}

export const sendOtp = (phone) => unwrap(icc.post("send-otp", { phone }));

export const verifyOtp = (phone, otp) =>
  unwrap(icc.post("verify-otp", { phone, otp })).then(saveSession);

export const register = (data) => unwrap(icc.post("register", data));

export const exchangeCode = (code) => unwrap(icc.post("exchange", { code })).then(saveSession);

let refreshing = null;

// Concurrent 401s share one rotation, since each refresh token can be used only once.
export function refreshSession() {
  const refreshToken = localStorage.getItem("refresh_token");
  if (!refreshToken) return Promise.reject(new Error("No session"));
  if (!refreshing) {
    refreshing = unwrap(icc.post("refresh", { refresh_token: refreshToken }))
      .then(saveSession)
      .finally(() => {
        refreshing = null;
      });
  }
  return refreshing;
}

// Ends one ICC session on the server without touching what this browser has stored.
export function revoke(refreshToken) {
  if (refreshToken) icc.post("logout", { refresh_token: refreshToken }).catch(() => {});
}

export function logout() {
  const refreshToken = localStorage.getItem("refresh_token");
  clearSession();
  revoke(refreshToken);
}

// Signing in from ICC: keeps this browser's sign-in when ICC sends the same number, and replaces it when the number differs.
export async function handoff(code, loadMe) {
  const keys = ["token", "refresh_token", "user", "account_id"];
  const kept = keys.map((key) => [key, localStorage.getItem(key)]);
  const restore = () => kept.forEach(([key, value]) => (value === null ? localStorage.removeItem(key) : localStorage.setItem(key, value)));
  const digits = (value) => String(value || "").replace(/\D/g, "").slice(-10);
  let current = null;
  try {
    current = JSON.parse(localStorage.getItem("user") || "null");
  } catch (e) {
    current = null;
  }
  const signedIn = localStorage.getItem("token") && current ? digits(current.phone) : "";
  // The old token stays in place while the code is exchanged, so the site never sees a signed-out moment.
  localStorage.removeItem("account_id");
  let session;
  let me;
  try {
    session = await exchangeCode(code);
    me = await loadMe();
  } catch (err) {
    if (session) revoke(session.refresh_token);
    restore();
    err.keptSignIn = Boolean(signedIn);
    throw err;
  }
  const number = digits(me.user && me.user.phone);
  if (signedIn && signedIn === number) {
    revoke(session.refresh_token);
    restore();
    return { kept: true, number, session, me };
  }
  revoke(kept[1][1]);
  localStorage.removeItem("user");
  return { kept: false, number, session, me };
}
